/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { map, reduce, values } from 'lodash';
import { Pricing } from 'blb-types/pricing';
const CLOSING_SUPPORT_EMAIL = 'closing@blendtitle.com';
export const FORM_NAME = 'ClosingFeesForm';
export const HEADER_HEIGHT = 174;

// Column Configs

export const paidByDropdownOptions = [{
  text: 'Borrower',
  value: 'Borrower'
}, {
  text: 'Lender',
  value: 'Lender'
}, {
  text: 'Seller',
  value: 'Seller'
}, {
  text: 'Other',
  value: 'Other'
}];
export const payeeDropdownOptions = [{
  text: 'Broker',
  value: 'Broker'
}, {
  text: 'Lender',
  value: 'Lender'
}, {
  text: 'Seller',
  value: 'Seller'
}, {
  text: 'Investor',
  value: 'Investor'
}, {
  text: 'Affiliate',
  value: 'Affiliate'
}, {
  text: 'Other',
  value: 'Other'
}];
export const paymentMilestoneDropdownOptions = [{
  text: 'Before Closing',
  value: 'BeforeClosing'
}, {
  text: 'At Closing',
  value: 'AtClosing'
}];
const SUBSECTION_COLUMN_CONFIG_DEFAULT = [{
  columnWidth: 21,
  componentType: 'GenericTextInput',
  headerDisplay: 'FEE NAME',
  isRequired: true,
  name: 'name',
  textInputType: 'text'
}, {
  columnWidth: 22,
  componentType: 'GenericDropdown',
  dropdownOptions: payeeDropdownOptions,
  headerDisplay: 'PAYEE',
  initialDropdownIdx: 0,
  name: 'paidTo'
}, {
  columnWidth: 13,
  componentType: 'GenericDropdown',
  dropdownOptions: paidByDropdownOptions,
  headerDisplay: 'PAID BY',
  initialDropdownIdx: 0,
  name: 'paidBy'
}, {
  columnWidth: 16,
  componentType: 'GenericDropdown',
  dropdownOptions: paymentMilestoneDropdownOptions,
  headerDisplay: 'WHEN',
  initialDropdownIdx: 0,
  name: 'paymentMilestone'
}, {
  columnWidth: 16,
  componentType: 'GenericTextInput',
  headerDisplay: 'AMOUNT',
  isRequired: true,
  name: 'amount',
  textInputType: 'moneyCents'
}];
const SUBSECTION_COLUMN_CONFIG_NO_PAYEE = [{
  columnWidth: 43,
  componentType: 'GenericTextInput',
  headerDisplay: 'FEE NAME',
  isRequired: true,
  name: 'name',
  textInputType: 'text'
}, {
  columnWidth: 13,
  componentType: 'GenericDropdown',
  dropdownOptions: paidByDropdownOptions,
  headerDisplay: 'PAID BY',
  initialDropdownIdx: 0,
  name: 'paidBy'
}, {
  columnWidth: 16,
  componentType: 'GenericDropdown',
  dropdownOptions: paymentMilestoneDropdownOptions,
  headerDisplay: 'WHEN',
  initialDropdownIdx: 0,
  name: 'paymentMilestone'
}, {
  columnWidth: 16,
  componentType: 'GenericTextInput',
  headerDisplay: 'AMOUNT',
  isRequired: true,
  name: 'amount',
  textInputType: 'moneyCents'
}];
const SUBSECTION_EXTENDED_COLUMN_CONFIG_DEFAULT = [{
  columnWidth: 88,
  componentType: 'GenericTextInput',
  name: 'newNote',
  placeholder: 'Note',
  textInputType: 'text'
}];
const SECTIONS = [{
  subsectionConfigs: [{
    key: Pricing.LoanEstimateSectionTypes.OriginationCharges,
    title: 'A. Origination Charges'
  }, {
    key: Pricing.LoanEstimateSectionTypes.ServicesNotShopped,
    title: 'B. Services Borrower Did Not Shop For'
  }, {
    key: Pricing.LoanEstimateSectionTypes.ServicesShopped,
    title: 'C. Services Borrower Did Shop For'
  }],
  title: 'Loan Costs'
}, {
  subsectionConfigs: [{
    key: Pricing.LoanEstimateSectionTypes.Taxes,
    title: 'E. Taxes and Other Government Fees'
  }, {
    key: Pricing.LoanEstimateSectionTypes.Prepaids,
    title: 'F. Prepaids'
  }, {
    key: Pricing.LoanEstimateSectionTypes.Escrow,
    title: 'G. Initial Escrow Payment at Closing'
  }, {
    key: Pricing.LoanEstimateSectionTypes.Other,
    title: 'H. Other'
  }],
  title: 'Other Costs'
}, {
  subsectionConfigs: [{
    key: Pricing.LoanEstimateSectionTypes.LenderCredits
  }],
  title: 'Lender Credits'
}, {
  subsectionConfigs: [{
    key: Pricing.LoanEstimateSectionTypes.Payoffs
  }],
  title: 'Payoffs and Payments'
}];
export const CLOSING_FEES_FORM_CONFIG = map(SECTIONS, _ref => {
  let {
    title,
    subsectionConfigs
  } = _ref;
  return {
    subsections: map(subsectionConfigs, _ref2 => {
      let {
        key,
        title: subsectionTitle
      } = _ref2;
      let subsectionColumnConfig;
      let hideTableAction = false;
      switch (key) {
        case Pricing.LoanEstimateSectionTypes.LenderCredits:
        case Pricing.LoanEstimateSectionTypes.Payoffs:
          hideTableAction = true;
        case Pricing.LoanEstimateSectionTypes.Escrow:
          subsectionColumnConfig = SUBSECTION_COLUMN_CONFIG_NO_PAYEE;
          break;
          break;
        default:
          subsectionColumnConfig = SUBSECTION_COLUMN_CONFIG_DEFAULT;
      }
      return {
        columnConfigs: subsectionColumnConfig,
        extendedColumnConfigs: SUBSECTION_EXTENDED_COLUMN_CONFIG_DEFAULT,
        hideTableAction,
        name: key,
        ...(subsectionTitle && {
          headerConfigs: {
            title: subsectionTitle
          }
        }),
        rowActionConfigs: []
      };
    }),
    title
  };
});

// Aggregation Constants

export const BASE_AGGREGATION_CONFIGS = reduce(values(Pricing.LoanEstimateSectionTypes), (acc, loanEstimateSectionType) => {
  acc[loanEstimateSectionType] = {
    aggregateKey: 'amount',
    subsectionKey: loanEstimateSectionType,
    ...(loanEstimateSectionType === 'LenderCredits' && {
      isNegative: true
    })
  };
  return acc;
}, {});
export const SECTION_AGGREGATION_CONFIGS = {
  LenderCredits: [BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.LenderCredits]],
  LoanCosts: [BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.OriginationCharges], BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.ServicesNotShopped], BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.ServicesShopped]],
  OtherCosts: [BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.Taxes], BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.Prepaids], BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.Escrow], BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.Other]],
  PayoffsAndPayments: [BASE_AGGREGATION_CONFIGS[Pricing.LoanEstimateSectionTypes.Payoffs]]
};
export const CUSTOM_AGGREGATION_CONFIGS = {
  ClosingCosts: [...SECTION_AGGREGATION_CONFIGS.LoanCosts, ...SECTION_AGGREGATION_CONFIGS.OtherCosts],
  TotalClosingCosts: [...SECTION_AGGREGATION_CONFIGS.LoanCosts, ...SECTION_AGGREGATION_CONFIGS.OtherCosts, ...SECTION_AGGREGATION_CONFIGS.LenderCredits]
};
export const CLOSING_DISCLOSURE_SECTIONS = {
  A: 'A. Origination Charges',
  B: 'B. Did Not Shop For',
  C: 'C. Did Shop For',
  D: 'D. Loan Costs',
  E: 'E. Taxes & Fees',
  F: 'F. Prepaids',
  G: 'G. Escrow',
  H: 'H. Other Charges',
  I: 'I. Other Costs',
  J: 'J. Total Closing Costs'
};
export const CLOSING_FEES_HEADERS = {
  BEFORE_CLOSING: 'Closing Costs Paid Before Closing',
  CASH_TO_CLOSE: 'Cash to Close',
  CLOSING_COSTS: 'Closing Costs',
  LENDER_CREDITS: 'Lender Credits',
  LOAN_AMOUNT: 'Loan Amount',
  TOTAL_CLOSING_COSTS: 'Total Closing Costs (J)',
  TOTAL_PAYOFFS: 'Total Payoffs and Payments (K)'
};
export const DEFAULT_REQUEST = {
  displayModal: false,
  rowIdx: -1,
  subsectionKey: Pricing.LoanEstimateSectionTypes.OriginationCharges,
  type: 'add'
};
export const REQUEST_MODAL_COPY = {
  add: {
    header: 'Add fee',
    subheader: 'Propose fee for loan team’s approval'
  },
  delete: {
    header: 'Propose fee delete',
    subheader: 'Propose delete for loan team’s approval'
  },
  edit: {
    header: 'Propose fee edit',
    subheader: 'Suggest fee edits for loan team’s approval'
  }
};
export const PENDING_FEE_NOTICE_COPY = {
  ADD: 'NEW FEE PROPOSED',
  DELETE: 'FEE DELETE REQUEST',
  EDIT: 'FEE EDIT REQUEST'
};
export const TYPE_TO_STATUS = {
  add: Pricing.UpdateType.ADD,
  delete: Pricing.UpdateType.DELETE,
  edit: Pricing.UpdateType.EDIT
};
export const ERROR_ALERT_MESSAGES = {
  AcceptFeesError: `Submit for approval failed: Looks like we hit a snag with the submitting your fee requests. Please try again later or, if this loan is closing soon, please reach out to ${CLOSING_SUPPORT_EMAIL} to collaborate on fees while we work to fix this.`,
  CreateRequestError: 'Failed to create request. If this problem persists, please contact the support team.',
  FeesImportError: 'Failed to fetch fee import data, please refresh the page to try again.',
  LoadingError: 'There was a problem loading the fees for this loan. Please refresh the page to try again.',
  LockError: 'This closing disclosure is currently under review with the loan team and not ready for collaboration.'
};
export const DEFAULT_ERROR_ALERT_MESSAGE = 'Please try again in a few minutes. If this problem persists, please contact the support team.';
export const INITIAL_CONTEXT_VALUES = {
  actionsEnabled: false,
  formInitialValues: {},
  formValues: {},
  loan: null,
  order: null,
  setCancelRequest: () => {},
  setCurrentRequest: () => {},
  setErrorType: () => {},
  setFormInitialValues: () => {},
  setFormValues: () => {},
  version: 1
};
export const CHECK_LOS_SYNC_TIME_INTERVAL = 60 * 1000; // 1 minute